import { AnimatePresence } from "framer-motion";
import { forwardRef, useContext, useImperativeHandle, useState } from "react";
import {
  MdOutlineSave,
  MdOutlineBookmarkAdd,
  MdOutlineBallot,
  MdOutlineAutoStories,
  MdOutlineCollectionsBookmark,
  MdOutlineFeaturedPlayList,
  MdAdd,
  MdOutlineExpandMore,
} from "react-icons/md";
import { DropdownItemProps } from "../components/dropdown-menu/DropdownItem";
import DropdownMenuPopUp from "../pop-up-panels/dropdown-menu-pop-up";
import { MainContext } from "components/utils/Contexts";

interface LessonsHeaderOptionsProps {
  ref: any;
  lessonsRef: React.MutableRefObject<any>;
}

const LessonsHeaderOptions: React.FC<LessonsHeaderOptionsProps> = forwardRef(
  ({ lessonsRef }, ref) => {
    // States definition
    const [showCreateNewPopUp, setShowCreateNewPopUp] = useState(false);
    const [showCreateNewButton, setShowCreateNewButton] = useState(true);
    const [showAddParentLessonButton, setShowAddParentLessonButton] =
      useState(false);
    const [addToButtonText, setAddToButtonText] = useState("");
    const [manageButtonText, setManageButtonText] = useState("");
    const [saveButton, setSaveButton] = useState<{
      show: boolean;
      disabled: boolean;
    }>({ show: false, disabled: true });
    const [addChildLessonButton, setAddChildLessonButton] = useState<{
      show: boolean;
      enabled: boolean;
    }>({ show: false, enabled: true });

    // Get the values from the context
    const { userRole } = useContext(MainContext);

    /**
     * Define the list of functions that can be used from external components
     * using a reference
     */
    useImperativeHandle(ref, () => ({
      setShowCreateNewButton(bool: boolean) {
        setShowCreateNewButton(bool);
      },
      setShowAddParentLessonButton(bool: boolean) {
        setShowAddParentLessonButton(bool);
      },
      // not using "options" format for parameters to prevent breaking legacy code
      setShowAddChildLessonButton(bool: boolean, enable: boolean = true) {
        setAddChildLessonButton({ show: bool, enabled: enable });
      },
      setSaveButton(options: { show: boolean; disabled: boolean }) {
        setSaveButton(options);
      },
      setShowCreateNewPopUp(bool: boolean) {
        setShowCreateNewPopUp(bool);
      },
      setAddToButtonText(string: string) {
        setAddToButtonText(string);
      },
      setManageButtonText(string: string) {
        setManageButtonText(string);
      },
    }));

    /**
     * Defining the options for the create button dropdown list
     */
    const createNewOptions: DropdownItemProps[] = [
      {
        text: "Program",
        icon: <MdOutlineFeaturedPlayList className="w-full h-full" />,
        onClickFunction: () => onCreateLesson("programs"),
      },
      {
        text: "Course",
        icon: <MdOutlineCollectionsBookmark className="w-full h-full" />,
        onClickFunction: () => onCreateLesson("courses"),
      },
      {
        text: "Project",
        icon: <MdOutlineAutoStories className="w-full h-full" />,
        onClickFunction: () => onCreateLesson("projects"),
      },
      {
        text: "Module",
        icon: <MdOutlineBallot className="w-full h-full" />,
        onClickFunction: () => onCreateLesson("modules"),
      },
    ];

    /**
     * Function that executes the create lessons
     * defined on lessons/index.tsx
     */
    const onCreateLesson = (
      lesson: "programs" | "courses" | "projects" | "modules"
    ) => {
      // Execute the function
      lessonsRef.current.onCreateLesson(lesson);
    };

    /**
     * Function that executes the press save button
     * defined on lessons/index.tsx
     */
    const handleSave = () => {
      // If the save button is available, then make it work
      if (!saveButton.disabled) lessonsRef.current.pressSaveButton();
    };

    return (
      <>
        {["master admin", "customer admin", "class admin"].includes(userRole) &&
          showCreateNewButton && (
            <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex relative">
              <div
                className="justify-center items-center gap-0.5 inline-flex cursor-pointer"
                onClick={() => {
                  if (!showCreateNewPopUp)
                    setShowCreateNewPopUp(!showCreateNewPopUp);
                }}
              >
                <div className="p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-300">
                  <div className="w-6 h-6 relative">
                    <MdAdd className="w-full h-full fill-secondary-600" />
                  </div>
                </div>
                <div className="w-4 h-4 relative">
                  <MdOutlineExpandMore className="w-4 h-4 text-secondary-600" />
                </div>
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap select-none">
                  Create New
                </div>
                <div className="w-4 h-4 px-[4.28px] opacity-0 justify-center items-center flex" />
              </div>
              <AnimatePresence>
                {showCreateNewPopUp && (
                  <DropdownMenuPopUp
                    containerStyle="absolute w-[154px] top-[90px] right-2 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-50"
                    optionsList={createNewOptions}
                    setShowPopUp={setShowCreateNewPopUp}
                  />
                )}
              </AnimatePresence>
            </div>
          )}
        {["master admin", "customer admin", "class admin"].includes(userRole) &&
          showAddParentLessonButton && (
            <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
              <div
                className="p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex cursor-pointer hover:bg-neutral-100 ease-in-out duration-300"
                onClick={() => lessonsRef.current.setShowAddToLesson(true)}
              >
                <MdOutlineBookmarkAdd className="w-6 h-6 fill-secondary-600" />
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className="text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide">
                  Add To {addToButtonText.slice(0, -1)}
                </div>
              </div>
            </div>
          )}
        {["master admin", "customer admin", "class admin"].includes(userRole) &&
          addChildLessonButton.show && (
            <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
              <div
                className={`p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex ${
                 addChildLessonButton.enabled
                    ? "cursor-pointer hover:bg-neutral-100 ease-in-out duration-150"
                    : "cursor-not-allowed"
                }`}
                onClick={addChildLessonButton.enabled ? () => lessonsRef.current.setShowAddPopUp(true) : () => {}}
                title={
                  addChildLessonButton.enabled 
                    ? ""
                    : "This user is not allowed to edit this lesson"
                }
              >
                <MdOutlineBallot className={`w-6 h-6 ${
                  addChildLessonButton.enabled 
                    ? "fill-secondary-600"
                    : "fill-neutral-200"
                }`} />
              </div>
              <div className="justify-center items-center gap-0.5 inline-flex">
                <div className={`text-center text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap select-none ${
                  addChildLessonButton.enabled
                    ? "text-secondary-600"
                    : "text-neutral-200"
                }`}>
                  Manage {manageButtonText}
                </div>
              </div>
            </div>
          )}
        {saveButton.show && (
          <div className="p-3 rounded-full flex-col justify-center items-center gap-2 inline-flex">
            <div
              className={
                saveButton.disabled
                  ? "justify-center items-center gap-0.5 inline-flex"
                  : "justify-center items-center gap-0.5 inline-flex cursor-pointer"
              }
              onClick={handleSave}
            >
              <div
                className={
                  saveButton.disabled
                    ? "p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex"
                    : "p-3 bg-neutral-50 rounded-full backdrop-blur-[32px] justify-center items-center gap-2 flex hover:bg-neutral-100 ease-in-out duration-300"
                }
              >
                <div className="w-6 h-6 relative">
                  <MdOutlineSave
                    className={
                      saveButton.disabled
                        ? "w-full h-full fill-neutral-300"
                        : "w-full h-full fill-secondary-600"
                    }
                  />
                </div>
              </div>
            </div>
            <div className="justify-center items-center gap-0.5 inline-flex">
              <div
                className={
                  saveButton.disabled
                    ? "text-center text-neutral-300 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap"
                    : "text-center text-secondary-600 text-xs font-semibold font-sans uppercase leading-[14px] tracking-wide whitespace-nowrap"
                }
              >
                Save
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default LessonsHeaderOptions;
